import { useRef, useState } from "react";
import styled from "styled-components";
import { Text } from "src/apps/components/typography/Texts";
import { useCreateDocumentationAreaActorInvitation } from "src/apps/hooks/useCreateDocumentationAreaActorInvitation";
import { useDocumentationArea } from "src/apps/hooks/useDocumentationArea";
import { useTranslations } from "src/apps/hooks/useTranslations";
import { HTTPError } from "src/contexts/_shared/domain/errors/HTTPError";
import { Icon, ICON_SIZES } from "src/apps/components/icon/Icon";
import { ICON_PATHS } from "src/apps/components/icon/IconSVGPaths";
import { Modal } from "src/apps/components/modal/Modal";

import { Input as ChakraInput, Spinner } from "@chakra-ui/react";
import { Button, colors } from "@permisso/permisso-components";

const MODAL_TITLE = "documentation_area.actor_invitation.modal.title";
const MODAL_BODY = "documentation_area.actor_invitation.modal.body";
const EMAIL_TITLE = "common.email.title";
const EMAIL_PLACEHOLDER = "common.email.placeholder";
const SUBMIT_BUTTON_TEXT = "documentation_area.actor_invitation.modal.submit";
const INVALID_EMAIL_ERROR = "common.error.invalid_email";
const GENERIC_ERROR = "common.error.generic";
const SUCCESS_TITLE = "documentation_area.actor_invitation.success.title";
const SUCCESS_MESSAGE = "documentation_area.actor_invitation.success.message";

const ERROR_CODE_MAP: Record<string, string> = {
  INVALID_EMAIL: INVALID_EMAIL_ERROR,
};

type ActorInvitationModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const ActorInvitationModal = ({ isOpen, onClose }: ActorInvitationModalProps) => {
  const { t } = useTranslations();
  const { selectedActorId, documentationArea } = useDocumentationArea();
  const { createActorInvitation, isPending } = useCreateDocumentationAreaActorInvitation();
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const initialFocusRef = useRef<HTMLButtonElement>(null);

  const actorId = selectedActorId ?? documentationArea?.getActor().getId();

  const resetAndClose = () => {
    setEmail("");
    setEmailError("");
    setShowSuccess(false);
    onClose();
  };

  const isEmailValid = (emailValue: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue);
  };

  const onSubmit = () => {
    if (!actorId) {
      return;
    }

    if (!isEmailValid(email)) {
      setEmailError(INVALID_EMAIL_ERROR);
      return;
    }

    createActorInvitation(
      {
        email: email.trim(),
        actorId,
      },
      {
        onSuccess: () => {
          setShowSuccess(true);
        },
        onError: (error) => {
          if (error instanceof HTTPError) {
            const code = error.getCode();
            if (code && ERROR_CODE_MAP[code]) {
              setEmailError(ERROR_CODE_MAP[code]);
              return;
            }
          }

          setEmailError(GENERIC_ERROR);
        },
      },
    );
  };

  const modalBodyContent = (
    <FormContainer>
      <Description>{t(MODAL_BODY)}</Description>
      <FieldContainer>
        <LabelRow>
          <FieldLabel>{t(EMAIL_TITLE)}</FieldLabel>
          <RequiredMark>*</RequiredMark>
        </LabelRow>
        <StyledInput
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
            setEmailError("");
          }}
          placeholder={t(EMAIL_PLACEHOLDER)}
          isInvalid={Boolean(emailError)}
        />
        {emailError ? <ErrorText>{t(emailError)}</ErrorText> : null}
      </FieldContainer>
    </FormContainer>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={resetAndClose}
      title={showSuccess ? null : t(MODAL_TITLE)}
      body={
        showSuccess ? (
          <SuccessContent>
            <SuccessIconRow>
              <Icon
                size={ICON_SIZES.s30}
                color={colors.defaultGreen}
                path={ICON_PATHS.CHECK_CIRCLE_THIN}
              />
            </SuccessIconRow>
            <SuccessTextGroup>
              <SuccessTitle>{t(SUCCESS_TITLE)}</SuccessTitle>
              <SuccessMessage>{t(SUCCESS_MESSAGE)}</SuccessMessage>
            </SuccessTextGroup>
          </SuccessContent>
        ) : (
          modalBodyContent
        )
      }
      secondaryButton={
        showSuccess ? null : (
          <Button ref={initialFocusRef} onClick={resetAndClose} variant="secondary">
            {t("common.cancel")}
          </Button>
        )
      }
      primaryButton={
        showSuccess ? null : (
          <Button onClick={onSubmit} isDisabled={!email || isPending} variant="primary">
            {isPending ? <Spinner size="sm" color={colors.grey0} /> : t(SUBMIT_BUTTON_TEXT)}
          </Button>
        )
      }
      initialFocusRef={initialFocusRef}
    />
  );
};

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Description = styled.p`
  margin: 0;
  color: #374151;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.5;
`;

const FieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const LabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const FieldLabel = styled.span`
  color: ${colors.grey800};
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.5;
`;

const RequiredMark = styled.span`
  color: #e44359;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.5;
`;

const StyledInput = styled(ChakraInput)`
  height: 2.5rem !important;
  border-radius: 0.5rem !important;
  border-color: ${colors.grey500} !important;
  padding: 0.25rem 0.5rem !important;
  font-size: 0.875rem;
  color: ${colors.grey800};

  &::placeholder {
    color: ${colors.grey500};
  }

  &:hover,
  &:focus-visible,
  &:focus {
    border-color: ${colors.grey500} !important;
    box-shadow: none !important;
  }
`;

const ErrorText = styled.span`
  color: ${colors.darkRed};
  font-size: 0.875rem;
  line-height: 1.5;
`;

const BaseActionButton = styled.button`
  min-width: 6.875rem;
  height: 2.5rem;
  padding: 0.5rem;
  border-radius: 0.5rem;
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.5;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border: 1px solid transparent;

  @media (max-width: 36rem) {
    width: 100%;
  }
`;

const SuccessContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SuccessIconRow = styled.div`
  display: flex;
  align-items: center;
  height: 2rem;
`;

const SuccessTextGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const SuccessTitle = styled.h2`
  margin: 0;
  color: ${colors.grey800};
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.5;
`;

const SuccessMessage = styled(Text)`
  margin: 0;
  color: #374151;
  font-size: 0.875rem;
  line-height: 1.5;
`;
